// src/Components/modules/Coordinacion/TrasladosColumn.js

import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Card, List, Typography, Spin, Checkbox, Button } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleBarReact from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import ItemTraslado from "./ItemTraslado";
import { useUser } from "../../../UserContext";
import PaginationControl from "./PaginationControl";

const { Text } = Typography;

const TrasladosColumn = ({
  traslados,
  handleContextMenu,
  onAsignarMovil,
  selectedMovil,
  setSelectedMovil,
  loading = false,
  selectedTraslados,
  setSelectedTraslados,
  selectedStatus,
  setSelectedStatus,
  filterChangeTrigger,
  // observacionesCounts removido - ahora viene del backend optimizado
  totalAbsoluto, // Total absoluto sin filtros (opcional)
}) => {
  // Estado para la paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Ref para el scroll container
  const scrollRef = useRef(null);

  // Función para hacer scroll al inicio (instantáneo)
  const scrollToTop = useCallback(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.getScrollElement();
      if (scrollElement) {
        scrollElement.scrollTop = 0;
      }
    }
  }, []);

  // Resetear a página 1 cuando "filterChangeTrigger" cambie
  useEffect(() => {
    setCurrentPage(1);
  }, [filterChangeTrigger]);

  // Estado para controlar el checkbox "Seleccionar Todos"
  const [selectAll, setSelectAll] = useState(false);

  // Con el permiso "Actualizar origen - destino" la casilla admite cualquier traslado (una sola selección para
  // ambas acciones). La asignación múltiple sigue exigiendo un grupo del mismo estado Programado (con móvil) o
  // Pre-Asignado: si la selección mezcla estados queda "Mixto" y esa opción se deshabilita en el menú.
  const { user: currentUser } = useUser();
  const puedeActualizarOrigenDestino = Boolean(
    currentUser?.modulos?.some((m) => m.key === "Coordinacion" && m.estado === "disponible") &&
      currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === "Coordinacion" && mp.permiso === "ACTUALIZAR_ORIGEN_DESTINO_CO"
      )
  );
  const estadoGrupo = (ids) => {
    if (ids.length === 0) return null;
    const estados = new Set(traslados.filter((t) => ids.includes(t.id)).map((t) => t.status));
    const [unico] = estados;
    if (estados.size === 1 && (unico === "Pre-Asignado" || (unico === "Programado" && selectedMovil))) {
      return unico;
    }
    return "Mixto";
  };
  const aplicarSeleccion = (ids) => {
    setSelectedTraslados(ids);
    setSelectedStatus(estadoGrupo(ids));
  };

  // Función para manejar la selección individual de traslado
  const handleSelectTraslado = (trasladoId, trasladoStatus) => {
    if (puedeActualizarOrigenDestino) {
      const yaSeleccionado = selectedTraslados.includes(trasladoId);
      const nuevos = yaSeleccionado
        ? selectedTraslados.filter((id) => id !== trasladoId)
        : [...selectedTraslados, trasladoId];
      if (selectedTraslados.length === 0 && trasladoStatus === "Pre-Asignado" && selectedMovil) {
        setSelectedMovil(null);
      }
      aplicarSeleccion(nuevos);
      return;
    }
    setSelectedTraslados((prevSelected) => {
      const isAlreadySelected = prevSelected.includes(trasladoId);

      if (isAlreadySelected) {
        const newSelected = prevSelected.filter((id) => id !== trasladoId);
        if (newSelected.length === 0) {
          setSelectedStatus(null);
        }
        return newSelected;
      }

      if (!["Programado", "Pre-Asignado"].includes(trasladoStatus)) {
        return prevSelected;
      }

      if (trasladoStatus === "Programado" && !selectedMovil) {
        return prevSelected;
      }

      if (prevSelected.length > 0 && trasladoStatus !== selectedStatus) {
        return prevSelected;
      }

      if (prevSelected.length === 0) {
        setSelectedStatus(trasladoStatus);
        if (trasladoStatus === "Pre-Asignado" && selectedMovil) {
          setSelectedMovil(null);
        }
      }

      return [...prevSelected, trasladoId];
    });
  };

  // Manejar el cambio del checkbox "Seleccionar Todos"
  const handleSelectAllChange = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (checked) {
      let statusToSelect = selectedStatus;

      if (!statusToSelect) {
        if (selectedMovil) {
          statusToSelect = "Programado";
          setSelectedStatus("Programado");
        } else {
          statusToSelect = "Pre-Asignado";
          setSelectedStatus("Pre-Asignado");
        }
      }

      if (statusToSelect === "Programado" && !selectedMovil) {
        return;
      }

      const idsToSelect = traslados
        .filter(
          (traslado) =>
            traslado.status === statusToSelect &&
            ["Programado", "Pre-Asignado"].includes(traslado.status)
        )
        .map((traslado) => traslado.id);

      setSelectedTraslados(idsToSelect);
    } else {
      setSelectedTraslados([]);
      setSelectedStatus(null);
    }
  };

  // Sincronizar el estado de selectAll con selectedTraslados
  useEffect(() => {
    const allSelectableTraslados = traslados.filter(
      (traslado) =>
        ["Programado", "Pre-Asignado"].includes(traslado.status) &&
        traslado.status === selectedStatus
    );
    if (
      selectedTraslados.length > 0 &&
      selectedTraslados.length === allSelectableTraslados.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedTraslados, traslados, selectedStatus]);

  // Calcular los índices de los elementos a mostrar en la página actual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTraslados = traslados.slice(indexOfFirstItem, indexOfLastItem);

  // Manejadores de cambio de página y cantidad de elementos por página
  const handleChangePage = (page) => {
    setCurrentPage(page);
    // Scroll al inicio al cambiar de página
    scrollToTop();
  };

  const handleItemsPerPageChange = (size) => {
    setItemsPerPage(size);
    setCurrentPage(1);
    // Scroll al inicio al cambiar cantidad por página
    scrollToTop();
  };

  // Definir el contenido a renderizar (cargando, lista o mensaje vacío)
  let content;

  if (loading) {
    const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
    content = (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          padding: "20px",
        }}
      >
        <Spin indicator={antIcon} />
        <Text style={{ marginLeft: 10 }}>Cargando traslados...</Text>
      </div>
    );
  } else if (traslados.length > 0) {
    content = (
      <List
        dataSource={currentTraslados}
        renderItem={(traslado) => (
          <ItemTraslado
            key={traslado.id}
            traslado={{
              ...traslado,
              distancia_estimada: Number(traslado.distancia_estimada),
              copago_diario: traslado.copago_diario
                ? Number(traslado.copago_diario)
                : 0,
              observaciones_count: traslado.observaciones_count || 0,
            }}
            handleContextMenu={handleContextMenu}
            onAsignarMovil={onAsignarMovil}
            isSelected={selectedTraslados.includes(traslado.id)}
            onSelect={() => handleSelectTraslado(traslado.id, traslado.status)}
            selectedMovil={selectedMovil}
            selectedStatus={selectedStatus}
          />
        )}
      />
    );
  } else {
    content = (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          padding: "20px",
        }}
      >
        <Text>No hay traslados disponibles.</Text>
      </div>
    );
  }

  return (
    <Card
      style={{
        padding: 0,
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {puedeActualizarOrigenDestino && traslados.length > 0 && (
        <SeleccionVisibles
          visibles={currentTraslados}
          seleccionados={selectedTraslados}
          aplicarSeleccion={aplicarSeleccion}
        />
      )}
      {selectedTraslados.length > 0 &&
        ["Programado", "Pre-Asignado"].includes(selectedStatus) && (
          <div
            style={{
              position: puedeActualizarOrigenDestino ? "static" : "absolute",
              top: "10px",
              left: "10px",
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              padding: "5px",
              borderRadius: "4px",
            }}
          >
            <Checkbox
              indeterminate={
                selectedTraslados.length > 0 &&
                selectedTraslados.length <
                  traslados.filter(
                    (t) =>
                      ["Programado", "Pre-Asignado"].includes(t.status) &&
                      t.status === selectedStatus
                  ).length
              }
              onChange={handleSelectAllChange}
              checked={selectAll}
              disabled={
                !(
                  (selectedStatus === "Programado" && selectedMovil) ||
                  selectedStatus === "Pre-Asignado"
                )
              }
            >
              {selectedStatus
                ? `Seleccionar Todos (${selectedStatus}) - ${selectedTraslados.length} seleccionados`
                : `Seleccionar Todos - ${selectedTraslados.length} seleccionados`}
            </Checkbox>
          </div>
        )}
      <SimpleBarReact
        ref={scrollRef}
        style={{
          height: "calc(100vh - 250px)",
          minHeight: "520px",
          overflowY: "auto",
          padding: "16px",
          paddingTop:
            !puedeActualizarOrigenDestino &&
            selectedTraslados.length > 0 &&
            ["Programado", "Pre-Asignado"].includes(selectedStatus)
              ? "80px"
              : "16px",
        }}
      >
        {content}
      </SimpleBarReact>
      <PaginationControl
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalItems={traslados.length}
        totalAbsoluto={totalAbsoluto}
        onPageChange={handleChangePage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onScrollToTop={scrollToTop}
      />
    </Card>
  );
};

// Casilla general: selecciona o quita los traslados visibles (página actual) sin tocar el resto de la selección
function SeleccionVisibles({ visibles, seleccionados, aplicarSeleccion }) {
  const idsVisibles = visibles.map((t) => t.id);
  const marcados = idsVisibles.filter((id) => seleccionados.includes(id)).length;
  const todos = idsVisibles.length > 0 && marcados === idsVisibles.length;
  const alternar = (e) => {
    aplicarSeleccion(
      e.target.checked
        ? Array.from(new Set([...seleccionados, ...idsVisibles]))
        : seleccionados.filter((id) => !idsVisibles.includes(id))
    );
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "8px 16px 0" }}>
      <Checkbox checked={todos} indeterminate={marcados > 0 && !todos} onChange={alternar}>
        Seleccionar visibles ({idsVisibles.length})
      </Checkbox>
      {seleccionados.length > 0 && (
        <>
          <Text type="secondary">{seleccionados.length} seleccionado(s)</Text>
          <Button size="small" type="link" onClick={() => aplicarSeleccion([])}>
            Limpiar selección
          </Button>
        </>
      )}
    </div>
  );
}

SeleccionVisibles.propTypes = {
  visibles: PropTypes.arrayOf(PropTypes.object).isRequired,
  seleccionados: PropTypes.arrayOf(PropTypes.number).isRequired,
  aplicarSeleccion: PropTypes.func.isRequired,
};

TrasladosColumn.propTypes = {
  traslados: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  handleContextMenu: PropTypes.func.isRequired,
  onAsignarMovil: PropTypes.func.isRequired,
  selectedMovil: PropTypes.object,
  setSelectedMovil: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  selectedTraslados: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  setSelectedTraslados: PropTypes.func.isRequired,
  selectedStatus: PropTypes.string,
  setSelectedStatus: PropTypes.func.isRequired,
  filterChangeTrigger: PropTypes.number, // Nuevo
  // observacionesCounts PropTypes removido - ahora viene del backend
  totalAbsoluto: PropTypes.number, // Total absoluto sin filtros (opcional)
};

export default TrasladosColumn;
